import { z } from "zod";

import { staffAccessSchema } from "../users/staff";

/**
 * Who the caller is, according to the token they just sent.
 *
 * The client asks for this on launch to restore a session and to learn which
 * sections to render, so it is the one payload the app caches and reads while
 * offline. That makes its shape worth pinning: a field silently disappearing
 * would surface as an app that opens to an empty shell during an outage, which
 * is the hardest possible place to diagnose it.
 */
export const meResponse = z.object({
  userId: z.string().uuid(),
  tenantId: z.string().uuid(),

  /** What the interface greets somebody with. */
  name: z.string(),

  /** How they sign in: a username for staff, a phone for owners. Absent from an identity saved before these were sent. */
  username: z.string().nullable().optional(),
  /** An owner's phone, when they have proven it. */
  phone: z.string().nullable().optional(),
  /** An owner's email, when they have proven it: their other way in. */
  email: z.string().nullable().optional(),

  role: z.enum(["admin", "staff"]),

  /**
   * Branches this person may work in. Every branch in the pharmacy for an
   * owner; only their assignments for staff.
   */
  branchIds: z.array(z.string().uuid()),

  /**
   * The permission switches, for rendering only. The interface hides what they
   * deny; the server refuses it regardless, and is the only boundary that
   * counts. An admin receives an all-true set rather than a special case.
   */
  access: staffAccessSchema,

  /**
   * The till this request came from, when there is one. Null for now — no
   * device is registered or required to sign in, and this is here for Phase 3,
   * where a till needs a receipt-number prefix.
   */
  deviceId: z.string().uuid().nullable(),

  /**
   * The pharmacy's name and phone, as printed on every receipt (a branch's own
   * phone, from GET /branches, comes first). Optional: an identity the phone
   * saved before these were sent has neither.
   */
  pharmacyName: z.string().optional(),
  pharmacyPhone: z.string().nullable().optional(),
});

export type MeResponse = z.infer<typeof meResponse>;
